import React, { useEffect, useImperativeHandle, forwardRef, useRef } from 'react';
import { useEditor, EditorContent, type Editor } from '@tiptap/react';
import { Extension } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import TextStyle from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import {
  Bold as BoldIcon,
  Italic as ItalicIcon,
  Underline as UnderlineIcon,
  Link as LinkIcon,
  List as BulletIcon,
  ListOrdered as OrderedIcon,
  Palette,
  Highlighter,
} from 'lucide-react';
import { VariableNode } from './VariableNode';
import { sanitizeEmailHtml, tokensToChips, chipsToTokens } from './emailHtmlSanitizer';
import type { VariableInfo } from './templateSections';

export interface ParagraphRichEditorHandle {
  insertVariable: (name: string) => void;
  focus: () => void;
}

interface Props {
  value: string;
  onChange: (html: string) => void;
  onFocus?: () => void;
  placeholder?: string;
  variables: VariableInfo[];
}

const TEXT_COLORS = ['#111827', '#7c3aed', '#2563eb', '#059669', '#dc2626', '#d97706'];
const HIGHLIGHT_COLORS = ['#fef3c7', '#fee2e2', '#dcfce7', '#dbeafe', '#ede9fe'];

const btnBase =
  'p-1.5 rounded hover:bg-gray-100 text-gray-600 transition-colors disabled:opacity-40';
const btnActive = 'bg-purple-100 text-purple-700';

// Enter inserts a hard break (<br>) so the user can break a line within a paragraph;
// inside lists we let the default behavior run (new <li>).
const EnterAsBreak = Extension.create({
  name: 'enterAsBreak',
  addKeyboardShortcuts() {
    return {
      Enter: ({ editor }) => {
        if (editor.isActive('listItem')) return false;
        return editor.chain().focus().setHardBreak().run();
      },
    };
  },
});

const Toolbar: React.FC<{
  editor: Editor;
  variables: VariableInfo[];
}> = ({ editor, variables }) => {
  const [colorOpen, setColorOpen] = React.useState(false);
  const [hlOpen, setHlOpen] = React.useState(false);
  const [varOpen, setVarOpen] = React.useState(false);

  const promptLink = () => {
    const prev = editor.getAttributes('link').href as string | undefined;
    const url = window.prompt('Link URL', prev || 'https://');
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().unsetLink().run();
      return;
    }
    const normalized = /^https?:\/\//i.test(url) ? url : `https://${url}`;
    editor.chain().focus().extendMarkRange('link').setLink({ href: normalized }).run();
  };

  return (
    <div className="flex flex-wrap items-center gap-0.5 px-2 py-1 border-b border-gray-200 bg-gray-50 rounded-t-lg">
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleBold().run()}
        className={`${btnBase} ${editor.isActive('bold') ? btnActive : ''}`}
        title="Bold"
      >
        <BoldIcon className="w-3.5 h-3.5" />
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleItalic().run()}
        className={`${btnBase} ${editor.isActive('italic') ? btnActive : ''}`}
        title="Italic"
      >
        <ItalicIcon className="w-3.5 h-3.5" />
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        className={`${btnBase} ${editor.isActive('underline') ? btnActive : ''}`}
        title="Underline"
      >
        <UnderlineIcon className="w-3.5 h-3.5" />
      </button>

      <span className="w-px h-4 bg-gray-300 mx-1" />

      <button
        type="button"
        onClick={promptLink}
        className={`${btnBase} ${editor.isActive('link') ? btnActive : ''}`}
        title="Link"
      >
        <LinkIcon className="w-3.5 h-3.5" />
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        className={`${btnBase} ${editor.isActive('bulletList') ? btnActive : ''}`}
        title="Bullet list"
      >
        <BulletIcon className="w-3.5 h-3.5" />
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        className={`${btnBase} ${editor.isActive('orderedList') ? btnActive : ''}`}
        title="Numbered list"
      >
        <OrderedIcon className="w-3.5 h-3.5" />
      </button>

      <span className="w-px h-4 bg-gray-300 mx-1" />

      <div className="relative">
        <button
          type="button"
          onClick={() => { setColorOpen((v) => !v); setHlOpen(false); setVarOpen(false); }}
          className={btnBase}
          title="Text color (may render differently in Outlook)"
        >
          <Palette className="w-3.5 h-3.5" />
        </button>
        {colorOpen && (
          <div className="absolute z-10 top-full left-0 mt-1 p-1.5 bg-white border border-gray-200 rounded shadow-lg flex gap-1">
            {TEXT_COLORS.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => {
                  editor.chain().focus().setColor(c).run();
                  setColorOpen(false);
                }}
                className="w-4 h-4 rounded-sm border border-gray-300"
                style={{ backgroundColor: c }}
                title={c}
              />
            ))}
            <button
              type="button"
              onClick={() => {
                editor.chain().focus().unsetColor().run();
                setColorOpen(false);
              }}
              className="w-4 h-4 rounded-sm border border-gray-300 text-[8px] text-gray-500"
              title="Clear"
            >
              ✕
            </button>
          </div>
        )}
      </div>

      <div className="relative">
        <button
          type="button"
          onClick={() => { setHlOpen((v) => !v); setColorOpen(false); setVarOpen(false); }}
          className={`${btnBase} ${editor.isActive('highlight') ? btnActive : ''}`}
          title="Highlight (may render differently in Outlook)"
        >
          <Highlighter className="w-3.5 h-3.5" />
        </button>
        {hlOpen && (
          <div className="absolute z-10 top-full left-0 mt-1 p-1.5 bg-white border border-gray-200 rounded shadow-lg flex gap-1">
            {HIGHLIGHT_COLORS.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => {
                  editor.chain().focus().setHighlight({ color: c }).run();
                  setHlOpen(false);
                }}
                className="w-4 h-4 rounded-sm border border-gray-300"
                style={{ backgroundColor: c }}
                title={c}
              />
            ))}
            <button
              type="button"
              onClick={() => {
                editor.chain().focus().unsetHighlight().run();
                setHlOpen(false);
              }}
              className="w-4 h-4 rounded-sm border border-gray-300 text-[8px] text-gray-500"
              title="Clear"
            >
              ✕
            </button>
          </div>
        )}
      </div>

      <span className="w-px h-4 bg-gray-300 mx-1" />

      <div className="relative">
        <button
          type="button"
          onClick={() => { setVarOpen((v) => !v); setColorOpen(false); setHlOpen(false); }}
          className={`${btnBase} text-[11px] font-semibold text-purple-700 hover:text-purple-900 px-2`}
          title="Insert variable"
        >
          {'{{ }}'}
        </button>
        {varOpen && (
          <div className="absolute z-10 top-full left-0 mt-1 p-1.5 bg-white border border-gray-200 rounded shadow-lg flex flex-col gap-0.5 max-h-60 overflow-y-auto min-w-[180px]">
            {variables.map((v) => (
              <button
                key={v.name}
                type="button"
                onClick={() => {
                  editor.chain().focus().insertVariable(v.name).run();
                  setVarOpen(false);
                }}
                className="text-left text-xs px-2 py-1 hover:bg-purple-50 rounded text-gray-700"
                title={v.description}
              >
                <span className="font-mono text-[10px] text-purple-500">{'{{'}</span>
                <span className="mx-0.5">{v.label}</span>
                <span className="font-mono text-[10px] text-purple-500">{'}}'}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const ParagraphRichEditor = forwardRef<ParagraphRichEditorHandle, Props>(
  ({ value, onChange, onFocus, placeholder, variables }, ref) => {
    const lastEmittedRef = useRef<string>(value);

    const editor = useEditor({
      extensions: [
        StarterKit.configure({
          heading: false,
          codeBlock: false,
          blockquote: false,
          horizontalRule: false,
          strike: false,
          code: false,
        }),
        Underline,
        TextStyle,
        Color,
        Highlight.configure({ multicolor: true }),
        Link.configure({
          openOnClick: false,
          autolink: true,
          protocols: ['http', 'https', 'mailto'],
          HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' },
        }),
        VariableNode,
        EnterAsBreak,
      ],
      content: tokensToChips(sanitizeEmailHtml(value || '')),
      editorProps: {
        attributes: {
          class:
            'prose prose-sm max-w-none px-3 py-2 min-h-[80px] focus:outline-none text-sm text-gray-800 whitespace-pre-wrap',
          'data-placeholder': placeholder || '',
        },
      },
      onUpdate: ({ editor: ed }) => {
        const raw = ed.getHTML();
        const withTokens = chipsToTokens(raw);
        const clean = sanitizeEmailHtml(withTokens);
        if (clean !== lastEmittedRef.current) {
          lastEmittedRef.current = clean;
          onChange(clean);
        }
      },
      onFocus: () => onFocus?.(),
    });

    // Sync external value changes (e.g. template type change, reset) into editor
    useEffect(() => {
      if (!editor) return;
      if (value === lastEmittedRef.current) return;
      lastEmittedRef.current = value;
      editor.commands.setContent(tokensToChips(sanitizeEmailHtml(value || '')), false);
    }, [value, editor]);

    useImperativeHandle(
      ref,
      () => ({
        insertVariable: (name: string) => {
          editor?.chain().focus().insertVariable(name).run();
        },
        focus: () => editor?.commands.focus(),
      }),
      [editor]
    );

    return (
      <div className="border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-purple-500 focus-within:border-transparent overflow-visible">
        {editor && <Toolbar editor={editor} variables={variables} />}
        <EditorContent editor={editor} />
      </div>
    );
  }
);

ParagraphRichEditor.displayName = 'ParagraphRichEditor';

export default ParagraphRichEditor;
